import { useEffect, useState, type ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import { api, ApiError, type Session } from '../api';
import { Button } from '../components/ui/button';
import { CardSkeleton, IntegrationSection } from '../components/integrations/IntegrationCard';
import { RilletCard } from '../components/integrations/RilletCard';
import { SequenceCard } from '../components/integrations/SequenceCard';
import { QuickBooksCard } from '../components/integrations/QuickBooksCard';
import { NetSuiteCard } from '../components/integrations/NetSuiteCard';
import { BankCard } from '../components/integrations/BankCard';
import { GmailCard } from '../components/integrations/GmailCard';
import { OutlookCard } from '../components/integrations/OutlookCard';
import { SlackCard } from '../components/integrations/SlackCard';
import { CallsCard } from '../components/integrations/CallsCard';
import { booksFreshness, booksInUse, booksLabel, gmailHealth, mailFreshness, netsuiteFreshness, netsuiteHealth, pageSummary, quickbooksHealth,
  recordLine, scheduleLine, slackHealth, sourceHealth, withFreshness, type BooksName, type GmailState, type Health,
  type IntegrationsState, type SlackState } from '../components/integrations/model';
import rilletLogo from '../assets/logos/rillet.png';
import sequenceLogo from '../assets/logos/sequence.svg';
import quickbooksLogo from '../assets/logos/quickbooks.svg';
import gmailLogo from '../assets/logos/gmail.svg';
import slackLogo from '../assets/logos/slack.svg';
import '../components/integrations/integrations.css';

const message = (e: unknown) => e instanceof Error ? e.message : 'This step could not be completed.';
type Loaded<T> = { value: T } | { error: string } | null;
const load = async <T,>(path: string): Promise<Loaded<T>> => {
  try { return { value: await api<T>(path) }; } catch (e) { return { error: message(e) }; }
};

function Failed({ name, error }: { name: string; error: string }) {
  return <div className="integration-card load-failed" role="alert"><strong>{name} status is unavailable.</strong>
    <p>{error} Reload the page to try again.</p></div>;
}

/**
 * Owner-only. Status first: a one-line health summary and the source of record, then groups (your books, email, team
 * chat, bank, calls). Books this instance doesn't use fold into "Other books you can connect" and don't count.
 */
/** `embedded` renders it as a section of Settings: no page landmark of its own, and its title one level down. */
export function Integrations({ session, embedded = false }: { session: Session; embedded?: boolean }) {
  const owner = session.actor.roles.includes('owner');
  const [sources, setSources] = useState<Loaded<IntegrationsState>>(null);
  const [gmail, setGmail] = useState<Loaded<GmailState>>(null);
  const [slack, setSlack] = useState<Loaded<SlackState>>(null);
  const [syncing, setSyncing] = useState(false); const [syncError, setSyncError] = useState('');
  const [, tick] = useState(0);
  const [testBusy, setTestBusy] = useState(false); const [testErr, setTestErr] = useState('');
  const [testStatus, setTestStatus] = useState<{ sentAt: string; replyReceivedAt: string | null } | null>(null);
  const reloadSources = async () => setSources(await load<IntegrationsState>('/app-api/integrations'));
  const reloadSlack = async () => setSlack(await load<SlackState>('/app-api/slack'));
  useEffect(() => {
    if (!owner) return;
    void reloadSources(); void load<GmailState>('/app-api/gmail').then(setGmail); void reloadSlack();
    const timer = window.setInterval(() => tick(n => n + 1), 30_000);
    return () => window.clearInterval(timer);
  }, [owner]);

  async function syncNow() {
    setSyncing(true); setSyncError('');
    try { await api('/app-api/sources/sync', {}); await reloadSources(); }
    catch (e) {
      setSyncError(e instanceof ApiError && e.status === 409 ? 'Nothing to sync yet, or a sync is already running.' : message(e));
    }
    finally { setSyncing(false); }
  }

  async function sendTestEmail() {
    setTestBusy(true); setTestErr('');
    try {
      await api<{ sent: boolean; messageId: string }>('/app-api/gmail/test', { requestId: crypto.randomUUID() });
      const status = await api<{ last?: { sentAt: string; replyReceivedAt: string | null } }>('/app-api/gmail/test');
      if (status.last) setTestStatus(status.last);
    } catch (e) { setTestErr(message(e)); } finally { setTestBusy(false); }
  }

  useEffect(() => {
    if (!testStatus || testStatus.replyReceivedAt) return;
    const start = Date.now();
    const pollInterval = setInterval(async () => {
      if (Date.now() - start > 600_000) { clearInterval(pollInterval); return; }
      try {
        const status = await api<{ last?: { sentAt: string; replyReceivedAt: string | null } }>('/app-api/gmail/test');
        if (status.last?.replyReceivedAt) { setTestStatus(status.last); clearInterval(pollInterval); }
      } catch { clearInterval(pollInterval); }
    }, 15_000);
    return () => clearInterval(pollInterval);
  }, [testStatus]);

  const state = sources && 'value' in sources ? sources.value : null;
  // The page summary counts the books this instance uses, plus email and team chat; books it doesn't use are offers.
  const available: BooksName[] = state ? ['netsuite', 'quickbooks', 'sequence', 'rillet']
    .filter((name): name is BooksName => name === 'netsuite' ? Boolean(state.netsuite) : name === 'quickbooks' ? Boolean(state.quickbooks) : true) : [];
  const used = state ? booksInUse(state) : [];
  const primaryBooks = !state ? [] : !state.live || !used.length ? available : available.filter(name => used.includes(name));
  const otherBooks = available.filter(name => !primaryBooks.includes(name));
  const books = state ? booksFreshness(state) : null;
  const bookHealth = (state: IntegrationsState, name: BooksName): Health => name === 'netsuite' ? withFreshness(netsuiteHealth(state.netsuite!), state.netsuite!.account ? netsuiteFreshness(state.netsuite!.lastDeliveryAt) : null)
      : name === 'quickbooks' ? withFreshness(quickbooksHealth(state, new URLSearchParams(window.location.search).get('quickbooks')), books)
        : withFreshness(sourceHealth(name, state), state.connectors[name].source === 'none' ? null : books);
  const healths: Health[] = state ? primaryBooks.map(name => bookHealth(state, name)) : [];
  if (gmail && 'value' in gmail) healths.push(gmail.value.accounts.length
    ? withFreshness(gmailHealth(gmail.value), mailFreshness(state?.mail)) : gmailHealth(gmail.value));
  if (slack && 'value' in slack) healths.push(slackHealth(slack.value));
  const freshestAt = [state?.last && state.last.status !== 'failed' && state.last.status !== 'not_configured' ? state.last.finishedAt : null,
    state?.netsuite?.lastDeliveryAt ?? null].filter((at): at is string => Boolean(at)).sort().at(-1) ?? null;
  const summary = !state ? '' : state.live && !used.length && !healths.some(h => h.tone === 'attention')
    ? 'Connect your books to get started.'
    : !state.live ? 'This host runs on demo books, so nothing is read from your systems.' : pageSummary(healths, freshestAt);
  const ready = Boolean(sources && gmail && slack);
  const stack = [rilletLogo, sequenceLogo, quickbooksLogo, gmailLogo, slackLogo];
  // Email & branding: settings cards about how Alder's emails look (for example the email signature) mount here.
  const emailBranding: ReactNode[] = [];
  const cardProps = state ? { state, onChanged: reloadSources, onSyncNow: () => void syncNow(), syncing } : null;
  const bookCard = (name: BooksName) => !state || !cardProps ? null
    : name === 'rillet' ? <RilletCard key={`rillet:${state.connectors.rillet.source}`} {...cardProps}/>
      : name === 'sequence' ? <SequenceCard key={`sequence:${state.connectors.sequence.source}`} {...cardProps}/>
        : name === 'quickbooks' ? <QuickBooksCard key={`quickbooks:${state.quickbooks!.source}`} {...cardProps}/>
          : <NetSuiteCard key="netsuite" status={state.netsuite!}/>;

  const Root = embedded ? 'section' : 'main'; const Title = embedded ? 'h2' : 'h1';
  return <Root id={embedded ? undefined : 'main-content'} className={`queue-page integrations-page${embedded ? ' settings-embedded' : ''}`}>
    <header className="integrations-header">
      <div className="integrations-title">
        <Title>Integrations</Title>
        <p className="integrations-health" role="status" aria-live="polite" data-testid="integrations-summary">
          <span className="logo-stack" aria-hidden>{stack.map(src => <img key={src} src={src} alt=""/>)}</span>
          {!owner ? 'Only an owner can manage integrations.' : ready && state ? <strong>{summary}</strong> : ready && sources && 'error' in sources
            ? 'Some connection status is unavailable.' : <span className="bone w-48"/>}
        </p>
        {/* On a demo host the summary above already says so; the record line is for live hosts. */}
        {owner && state?.live && <p className="integrations-record" data-testid="integrations-record">{recordLine(state)}</p>}
      </div>
      {owner && <div className="integrations-sync">
        {(!state || state.live) && <p>{!state ? <span className="bone w-40"/> : scheduleLine(state.schedule)}</p>}
        <Button variant="outline" size="sm" disabled={syncing || !state?.live} onClick={() => void syncNow()}
          title={state && !state.live ? 'This host uses demo data, so there is nothing to sync.' : undefined}>
          <RefreshCw aria-hidden className={syncing ? 'spin' : undefined}/>{syncing ? 'Syncing…' : 'Sync now'}</Button>
      </div>}
    </header>
    {syncError && <div className="alert error" role="alert">{syncError}</div>}
    {!owner ? <div className="alert notice">Ask an owner to connect Rillet, Sequence, QuickBooks, NetSuite, Gmail or Slack.</div> : <>
      <IntegrationSection id="books" title="Your books" description="Where Alder reads invoices, customers and payments.">
        {!sources ? <><CardSkeleton/><CardSkeleton/></> : 'error' in sources ? <Failed name="Rillet, Sequence, QuickBooks and NetSuite"
          error={sources.error}/> : cardProps && primaryBooks.map(name => bookCard(name))}
      </IntegrationSection>
      {otherBooks.length > 0 && <details className="integration-more" data-testid="other-books">
        <summary>Other books you can connect <span>{otherBooks.map(name => booksLabel[name]).join(', ')}</span></summary>
        <div className="integration-grid">{otherBooks.map(name => bookCard(name))}</div>
      </details>}
      <div className="integration-pair">
        <IntegrationSection id="email" title="Email" description="Where reminders go out and replies come in.">
          {!gmail ? <CardSkeleton/> : 'error' in gmail ? <Failed name="Gmail" error={gmail.error}/>
            : <GmailCard state={gmail.value} {...(state?.mail ? { mail: state.mail } : {})}
              {...(state?.mailboxAuth ? { auth: state.mailboxAuth } : {})}
              {...(state?.mailboxHealth ? { sending: state.mailboxHealth } : {})}
              {...(state?.sendMode ? { sendMode: state.sendMode } : {})} shadowMode={Boolean(state?.shadowMode)}
              actions={gmail.value.accounts.length && owner ? <Button variant="outline" size="sm"
                disabled={testBusy} onClick={() => void sendTestEmail()}>Send test email</Button> : null}>
              {testStatus && (() => {
                const fmt = (d: string) => new Date(d).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                return <div className="test-status" role="status" aria-live="polite">
                  {testStatus.replyReceivedAt
                    ? `Reply received ${fmt(testStatus.replyReceivedAt)} — Gmail send and reply intake work.`
                    : `Test email sent ${fmt(testStatus.sentAt)} · waiting for your reply`}
                </div>;
              })()}
              {testErr && <div className="test-error" role="alert" style={{ color: 'var(--color-error)' }}>{testErr}</div>}
            </GmailCard>}
          <OutlookCard/>
        </IntegrationSection>
        <IntegrationSection id="team" title="Team chat" description="Where your team answers questions and approvals.">
          {!slack ? <CardSkeleton/> : 'error' in slack ? <Failed name="Slack" error={slack.error}/>
            : <SlackCard state={slack.value} onChanged={reloadSlack} {...(state?.sendMode ? { sendMode: state.sendMode } : {})}/>}
        </IntegrationSection>
      </div>
      <div className="integration-pair">
        <IntegrationSection id="bank" title="Bank" description="Where customer payments arrive, to match to invoices.">
          <BankCard/>
        </IntegrationSection>
        {state?.voiceMode && state.voiceMode !== 'off' && <IntegrationSection id="calls" title="Calls"
          description="Phone follow-up when email goes unanswered."><CallsCard mode={state.voiceMode}/></IntegrationSection>}
      </div>
      {emailBranding.length > 0 && <IntegrationSection id="email-branding" title="Email & branding"
        description="How the emails Alder sends look to your customers.">{emailBranding}</IntegrationSection>}
    </>}
  </Root>;
}
